import { frameFormatRatio } from "@/lib/frame-format-core"
import { flowNodeEntityKind, type FlowNodeKind, type FlowNodeRow } from "@/types/flow"

/** Длинная сторона превью ноды. Короткая следует из соотношения сторон. */
export const FLOW_NODE_LONG_SIDE = 360
export const FLOW_NODE_MIN_LONG_SIDE = 180
export const FLOW_NODE_MAX_LONG_SIDE = 960

/** Крайние файлы не растягивают ноду в полоску. */
export const FLOW_NODE_MIN_RATIO = 1 / 3
export const FLOW_NODE_MAX_RATIO = 3

const FALLBACK_RATIO = 16 / 9

export type FlowNodeAspectSource = Pick<FlowNodeRow, "aspect_ratio" | "media_width" | "media_height">

export function parseFlowAspectRatio(input?: string | null): number | null {
  const match = /^(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)$/.exec((input ?? "").trim())
  if (!match) return null
  const width = Number(match[1])
  const height = Number(match[2])
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null
  return width / height
}

/** Пиксели файла важнее строки формата. Без обоих — 16:9. */
export function flowNodeAspectRatio(node: FlowNodeAspectSource): number {
  const width = node.media_width
  const height = node.media_height
  if (width != null && height != null && width > 0 && height > 0) return width / height
  return parseFlowAspectRatio(node.aspect_ratio) ?? FALLBACK_RATIO
}

export function clampFlowNodeRatio(ratio: number): number {
  if (!Number.isFinite(ratio) || ratio <= 0) return FALLBACK_RATIO
  return Math.min(FLOW_NODE_MAX_RATIO, Math.max(FLOW_NODE_MIN_RATIO, ratio))
}

/** Ширина карточки: по умолчанию длинная сторона 360, после ресайза — `displayWidth` в пределах 180–960. */
export function flowNodeWidth(ratio: number, displayWidth?: number | null): number {
  const widthShare = Math.min(1, clampFlowNodeRatio(ratio))
  const longSide = displayWidth ? displayWidth / widthShare : FLOW_NODE_LONG_SIDE
  return Math.round(Math.min(FLOW_NODE_MAX_LONG_SIDE, Math.max(FLOW_NODE_MIN_LONG_SIDE, longSide)) * widthShare)
}

/** Рамка карточки: `border` в flow-node-card. В высоту ноды входит, подпись над карточкой — нет. */
const FLOW_NODE_BORDER_PX = 1

/** Кружок связи: `.flow-handle` в flow-canvas.css. Ребро садится на внешний край, не в центр. */
export const FLOW_HANDLE_SIZE = 16

/** Пустая нода: как `fallback_aspect_ratio` в payload. Сущности — квадрат, генерация и шот — формат проекта. */
const PROJECT_FRAME_KINDS = new Set<FlowNodeKind>([
  "image",
  "video",
  "video_transition",
  "video_upscale",
  "video_depth",
  "chain",
  "shot",
])

export function flowFreshNodeRatio(kind: FlowNodeKind, frameFormat?: string | null): number {
  if (flowNodeEntityKind(kind)) return 1
  if (PROJECT_FRAME_KINDS.has(kind)) return frameFormatRatio(frameFormat)
  return FALLBACK_RATIO
}

/** Ширина и высота карточки до измерения DOM: aspect-box плюс рамка сверху и снизу. */
export function flowNodeCardSize(ratio: number, displayWidth?: number | null): { width: number; height: number } {
  const clamped = clampFlowNodeRatio(ratio)
  const width = flowNodeWidth(clamped, displayWidth)
  const inner = Math.max(0, width - FLOW_NODE_BORDER_PX * 2)
  return { width, height: inner / clamped + FLOW_NODE_BORDER_PX * 2 }
}

export type FlowConnectHandleSide = "left" | "right"

/**
 * Левый верхний угол ноды, чтобы внешний край кружка (точка ребра) совпал с концом связи.
 * Слева кружок принимает связь, справа — отдаёт.
 */
export function flowConnectNodeOrigin(
  drop: { x: number; y: number },
  size: { width: number; height: number },
  side: FlowConnectHandleSide,
): { x: number; y: number } {
  const radius = FLOW_HANDLE_SIZE / 2
  const y = drop.y - size.height / 2
  if (side === "left") return { x: drop.x + radius, y }
  return { x: drop.x - size.width - radius, y }
}

/** `out` — новая нода принимает связь слева, `in` — отдаёт справа. */
export function flowConnectHandleSide(direction: "in" | "out"): FlowConnectHandleSide {
  return direction === "out" ? "left" : "right"
}

/** Точка ноды по её фактическому кадру, а не по прогнозу пустой карточки. */
export function flowConnectOriginForNode(
  drop: { x: number; y: number },
  node: FlowNodeAspectSource & { display_width?: number | null },
  side: FlowConnectHandleSide,
): { x: number; y: number } {
  return flowConnectNodeOrigin(drop, flowNodeCardSize(flowNodeAspectRatio(node), node.display_width), side)
}
